import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import {
  ArrowUUpLeftIcon,
  SealCheckIcon,
  TagIcon,
  TruckIcon,
  type Icon,
} from '@phosphor-icons/react';
import Tooltip from '_components/UI/Tooltip';
import { restrictedReturnValueProp } from '@utils/pdp/defaultPDPValueProps';
import SimpleLink from '_components/UI/SimpleLink';

import styles from './PurchaseGuarantee.module.scss';

type PurchaseGuaranteeKey =
  | 'shipping'
  | 'returns'
  | 'satisfaction'
  | 'bestPrice';

interface PurchaseGuaranteeItem {
  key: PurchaseGuaranteeKey;
  icon: Icon;
  title: string;
  tooltip?: string;
  href?: string;
}

const GUARANTEE_ICON_MAP: Record<PurchaseGuaranteeKey, Icon> = {
  shipping: TruckIcon,
  returns: ArrowUUpLeftIcon,
  satisfaction: SealCheckIcon,
  bestPrice: TagIcon,
};

const PURCHASE_GUARANTEES: PurchaseGuaranteeKey[] = [
  'shipping',
  'returns',
  'satisfaction',
  'bestPrice',
];

const mapGuarantees = (
  t: (key: string) => string,
  locale: string,
  hasReturnRestriction: boolean
): PurchaseGuaranteeItem[] =>
  PURCHASE_GUARANTEES.map((key) => {
    if (key === 'returns' && hasReturnRestriction) {
      const restrictedProp =
        locale === 'en'
          ? restrictedReturnValueProp.en
          : restrictedReturnValueProp.fr;
      return {
        key,
        icon: GUARANTEE_ICON_MAP[key],
        title: restrictedProp.title,
        href: `/${locale}/${restrictedProp.linkUrl}`,
      };
    }
    return {
      key,
      icon: GUARANTEE_ICON_MAP[key],
      title: t(`purchaseGuarantee.${key}.title`),
      tooltip: t(`purchaseGuarantee.${key}.tooltip`),
    };
  });

const PurchaseGuarantee = ({
  hasReturnRestriction = false,
}: {
  hasReturnRestriction?: boolean;
}) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const locale = (router.query?.locale as string) || router.locale || 'en';
  const guarantees = mapGuarantees(t, locale, hasReturnRestriction);

  return (
    <section
      className={styles.purchaseGuarantee}
      aria-label={t('purchaseGuarantee.ariaLabel')}
    >
      <ul className={styles.list}>
        {guarantees.map(
          ({ key, icon: IconComponent, title, tooltip, href }) => (
            <li key={key} className={styles.listItem}>
              <span className={styles.icon} aria-hidden="true">
                <IconComponent size={20} weight="light" />
              </span>
              <span>
                {href ? (
                  <SimpleLink
                    className={styles.title}
                    href={href}
                    variant="secondary"
                    size="small"
                  >
                    {title}
                  </SimpleLink>
                ) : (
                  <span className={styles.title}>{title}</span>
                )}
                {tooltip && (
                  <span className={styles.tooltipInline}>
                    <Tooltip
                      className={styles.tooltipWrapper}
                      content={tooltip}
                      id={`purchase-guarantee-tooltip-${key}`}
                      iconSize={16}
                    />
                  </span>
                )}
              </span>
            </li>
          )
        )}
      </ul>
    </section>
  );
};

export default PurchaseGuarantee;
